import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, BookOpen, PlayCircle, Cpu } from "lucide-react";

export default function MLLab() {
  const [model, setModel] = useState(null);
  const [training, setTraining] = useState(false);
  const [useSynth, setUseSynth] = useState(true);
  const [nSamples, setNSamples] = useState(500);
  const [purgeGap, setPurgeGap] = useState(3);

  const loadModel = async () => {
    try {
      const m = await api.mlModel();
      setModel(m);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadModel(); }, []);

  const train = async () => {
    setTraining(true);
    try {
      const res = await api.mlTrain(useSynth, nSamples, purgeGap);
      if (res.ok) {
        setModel(res);
        toast.success(`Modèle entraîné (CV accuracy ${(res.cv_avg_accuracy * 100).toFixed(1)}%)`);
      } else {
        toast.error(res.reason || "Erreur");
      }
    } catch (e) { toast.error("Erreur entraînement"); }
    finally { setTraining(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="ml-lab-page">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight">ML Lab — Entraînement & Validation</h1>
        <p className="text-white/50 text-sm mt-1">Régression logistique avec Purged K-Fold CV + Deflated Sharpe. Aucun coefficient hardcodé.</p>
      </div>

      {/* Training controls */}
      <div className="panel p-5">
        <div className="panel-title mb-4"><PlayCircle size={14} className="inline mr-2" />Entraîner le modèle</div>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={useSynth} onChange={(e) => setUseSynth(e.target.checked)} data-testid="ml-use-synth" />
            Dataset synthétique (démo — trades réels sinon)
          </label>
          <div>
            <label className="label">Nombre d'échantillons</label>
            <input type="number" className="field w-28" value={nSamples} onChange={(e) => setNSamples(parseInt(e.target.value))} disabled={!useSynth} data-testid="ml-n-samples" />
          </div>
          <div>
            <label className="label" title="Nombre de trades purgés autour de chaque bloc de test (purge + embargo). Sur trades réels, la purge canonique par chevauchement d'intervalles (AFML ch.7) est utilisée automatiquement.">Purge / embargo (± trades)</label>
            <input type="number" min="0" max="50" className="field w-28" value={purgeGap} onChange={(e) => setPurgeGap(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))} data-testid="ml-purge-gap" />
          </div>
          <button className="btn btn-primary" onClick={train} disabled={training} data-testid="ml-train-btn">
            {training ? <Loader2 size={14} className="animate-spin" /> : <Cpu size={14} />} Entraîner
          </button>
          <button className="btn" onClick={loadModel} data-testid="ml-refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* Model output */}
      {model?.ok || model?.trained !== false ? (
        model?.ok || model?.cv_avg_accuracy != null ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="CV Accuracy" value={`${(model.cv_avg_accuracy * 100).toFixed(1)}%`} highlight />
              <StatCard label="Log Loss" value={model.cv_avg_log_loss?.toFixed(4)} />
              <StatCard label="Brier Score" value={model.cv_avg_brier?.toFixed(4)} />
              <StatCard label="Samples" value={model.n_samples} />
            </div>

            <div className="panel p-5">
              <div className="panel-title mb-4"><BookOpen size={14} className="inline mr-2" />Feature Importance</div>
              <div className="space-y-2" data-testid="ml-feature-importance">
                {model.feature_importance?.slice(0, 12).map((f, i) => {
                  const maxW = Math.max(...model.feature_importance.map((x) => x.abs_weight));
                  const barW = (f.abs_weight / maxW) * 100;
                  const isPos = f.weight > 0;
                  return (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <div className="w-40 font-mono text-xs text-white/60 truncate">{f.feature}</div>
                      <div className="flex-1 h-4 bg-white/5 rounded relative overflow-hidden">
                        <div className={`absolute inset-y-0 ${isPos ? "left-1/2 bg-up/50" : "right-1/2 bg-down/50"}`} style={{ width: `${barW / 2}%` }} />
                        <div className="absolute inset-y-0 left-1/2 w-px bg-white/20" />
                      </div>
                      <div className={`w-16 text-right font-mono text-xs ${isPos ? "text-up" : "text-down"}`}>
                        {f.weight > 0 ? "+" : ""}{f.weight}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="panel p-5">
              <div className="panel-title mb-3">Metrics par fold (CV)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ml-fold-metrics">
                  <thead><tr className="text-white/50 text-xs">
                    <th className="text-left py-2">Fold</th>
                    <th className="text-right">Accuracy</th>
                    <th className="text-right">Log Loss</th>
                    <th className="text-right">Brier</th>
                  </tr></thead>
                  <tbody>
                    {model.fold_metrics?.map((f, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="py-2">Fold {f.fold + 1}</td>
                        <td className="text-right font-mono">{(f.accuracy * 100).toFixed(1)}%</td>
                        <td className="text-right font-mono">{f.log_loss.toFixed(4)}</td>
                        <td className="text-right font-mono">{f.brier.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-white/50 mt-3">
                Entraîné le : {model.trained_at ? new Date(model.trained_at).toLocaleString("fr-FR") : "—"}
                {" · "}Hyperparams : lr={model.hyperparameters?.lr}, epochs={model.hyperparameters?.n_epochs}, l2={model.hyperparameters?.l2}, K={model.hyperparameters?.k_folds}
                {" · "}Purge : {model.hyperparameters?.purge_method === "label_overlap" ? `chevauchement d'intervalles (embargo ${(model.hyperparameters?.embargo_pct * 100).toFixed(0)}%)` : `±${model.hyperparameters?.purge_gap} trades`}
              </div>
            </div>
          </>
        ) : (
          <div className="panel p-5 text-white/40 text-sm" data-testid="ml-no-model">
            {model?.message || "Aucun modèle entraîné."}
          </div>
        )
      ) : (
        <div className="panel p-5 text-white/40 text-sm" data-testid="ml-no-model">Aucun modèle entraîné pour le moment.</div>
      )}
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`p-4 rounded ${highlight ? "bg-accent-blue/10 border border-accent-blue/30" : "bg-white/5"}`}>
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-mono text-xl mt-2 font-bold ${highlight ? "text-accent-blue" : ""}`}>{value}</div>
    </div>
  );
}
